// src/pages/rh/DetailContrat.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
    Card, Row, Col, Button, Badge, Spinner, Alert,
    Modal, Table, Form, Tooltip, OverlayTrigger, ProgressBar
} from "react-bootstrap";
import { useNavigate, useParams, Link } from "react-router-dom";
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import { formatDate, formatCurrency } from "../../utils/formatters";
import { API_BASE_URL } from "../../constants/api";
import { COLORS } from "../../constants/colors";
import logo from "../../assets/img/IMG-20250130-WA0008.jpg";

/* ============================================================
   HELPERS DE COMPARAISON DE DATES (sans heure)
   ============================================================ */
const getDateOnly = (dateInput) => {
    if (!dateInput) return null;
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d;
};

const isBeforeToday = (dateInput) => {
    const d = getDateOnly(dateInput);
    if (!d) return false;
    const today = getDateOnly(new Date());
    return d < today;
};

const isAfterToday = (dateInput) => {
    const d = getDateOnly(dateInput);
    if (!d) return false;
    const today = getDateOnly(new Date());
    return d > today;
};

const isToday = (dateInput) => {
    const d = getDateOnly(dateInput);
    if (!d) return false;
    const today = getDateOnly(new Date());
    return d.getTime() === today.getTime();
};

const daysBetween = (dateInput1, dateInput2 = new Date()) => {
    const d1 = getDateOnly(dateInput1);
    const d2 = getDateOnly(dateInput2);
    if (!d1 || !d2) return 0;
    return Math.round((d1 - d2) / (1000 * 60 * 60 * 24));
};

/* ============================================================
   TOAST
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    useEffect(() => {
        if (show && autoHideDelay) {
            const t = setTimeout(onClose, autoHideDelay);
            return () => clearTimeout(t);
        }
    }, [show, autoHideDelay, onClose]);

    if (!show) return null;
    const cfg = {
        success: { bg: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bg: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bg: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bg: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bg: COLORS.info, icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: cfg.bg }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${cfg.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: cfg.bg, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   INFO ROW
   ============================================================ */
const InfoRow = ({ icon, label, value, color }) => (
    <div className="detail-info-row">
        <div className="detail-info-icon" style={{ background: `${color || COLORS.primary}15`, color: color || COLORS.primary }}>
            <i className={icon}></i>
        </div>
        <div className="flex-grow-1 min-width-0">
            <div className="detail-info-label">{label}</div>
            <div className="detail-info-value">{value || '—'}</div>
        </div>
    </div>
);

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function DetailContrat() {
    const navigate = useNavigate();
    const { id } = useParams();

    const [contrat, setContrat] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [societeInfos, setSocieteInfos] = useState(null);
    const [logoBase64, setLogoBase64] = useState('');
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => {
        fetchContrat();
        fetchSocieteInfos();
        loadLogoBase64();
    }, [id]);

    /* --------- API --------- */
    const fetchSocieteInfos = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=societe`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) setSocieteInfos(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement société", err);
        }
    };

    const loadLogoBase64 = () => {
        return new Promise((resolve) => {
            try {
                const img = new Image();
                img.crossOrigin = "Anonymous";
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    const dataURL = canvas.toDataURL('image/jpeg');
                    setLogoBase64(dataURL);
                    resolve(dataURL);
                };
                img.onerror = () => { setLogoBase64(''); resolve(''); };
                img.src = logo;
            } catch { setLogoBase64(''); resolve(''); }
        });
    };

    const fetchContrat = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/contrats.php?id=${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setContrat(result.data);
            setEditForm(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (field, fallbackFileName) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/contrats.php?action=download&id=${id}&field=${field}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) {
                const err = await response.json();
                showToast('error', 'Erreur', err.message || 'Fichier introuvable');
                return;
            }
            const cd = response.headers.get('Content-Disposition');
            let fileName = fallbackFileName;
            if (cd) {
                const match = cd.match(/filename="?([^"]+)"?/);
                if (match) fileName = match[1];
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            showToast('success', 'Téléchargement', `${fileName} téléchargé`);
        } catch (err) {
            console.error(err);
            showToast('error', 'Erreur', 'Impossible de télécharger le fichier');
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/contrats.php?id=${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) {
                setShowDeleteModal(false);
                showToast('success', 'Succès', 'Contrat supprimé');
                setTimeout(() => navigate('/rh/contrats'), 1500);
            } else {
                setError(result.message);
                setShowDeleteModal(false);
            }
        } catch {
            setError("Erreur lors de la suppression");
            setShowDeleteModal(false);
        } finally {
            setDeleting(false);
        }
    };

    const handleEdit = async () => {
        setEditing(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/contrats.php?id=${id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(editForm)
            });
            const result = await response.json();
            if (result.success) {
                setShowEditModal(false);
                fetchContrat();
                showToast('success', 'Modifié', 'Contrat mis à jour avec succès');
            } else {
                showToast('error', 'Erreur', result.message);
            }
        } catch {
            showToast('error', 'Erreur', 'Erreur lors de la modification');
        } finally {
            setEditing(false);
        }
    };

    const handleEditChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
        }));
    };

    /* --------- CALCULS (CORRIGÉS) --------- */
    const getStatutBadge = () => {
        if (!contrat) return { gradient: '#64748b', text: 'Inconnu', icon: 'ri-question-line' };

        // 1️⃣ Statut forcé en BDD
        if (contrat.statut === 'termine') {
            return { gradient: 'linear-gradient(135deg, #64748b, #475569)', text: 'Terminé', icon: 'ri-checkbox-circle-line' };
        }
        if (contrat.statut === 'expire') {
            return { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', text: 'Expiré', icon: 'ri-error-warning-line' };
        }

        // 2️⃣ Comparaison basée sur les DATES uniquement (sans heure)
        if (contrat.date_fin && isBeforeToday(contrat.date_fin)) {
            return { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', text: 'Expiré', icon: 'ri-error-warning-line' };
        }
        if (contrat.date_debut && isAfterToday(contrat.date_debut)) {
            return { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', text: 'À venir', icon: 'ri-time-line' };
        }
        return { gradient: 'linear-gradient(135deg, #10b981, #059669)', text: 'Actif', icon: 'ri-checkbox-circle-line' };
    };

    const getJoursRestants = () => {
        if (!contrat || !contrat.date_fin) return null;
        return daysBetween(contrat.date_fin, new Date());
    };

    const getDureeContrat = () => {
        if (!contrat) return '';
        const dateDebut = new Date(contrat.date_debut);
        if (!contrat.date_fin) return 'Durée indéterminée (CDI)';
        const dateFin = new Date(contrat.date_fin);
        const diffJours = Math.ceil(Math.abs(dateFin - dateDebut) / (1000 * 60 * 60 * 24)) + 1;
        const mois = Math.floor(diffJours / 30);
        const jours = diffJours % 30;
        return mois > 0 ? `${mois} mois ${jours > 0 ? jours + ' j' : ''}` : `${diffJours} jours`;
    };

    const getProgressPeriod = () => {
        if (!contrat || !contrat.date_fin) return 100;
        const d1 = getDateOnly(contrat.date_debut)?.getTime();
        const d2 = getDateOnly(contrat.date_fin)?.getTime();
        const now = getDateOnly(new Date())?.getTime();
        if (!d1 || !d2 || !now) return 0;
        if (now <= d1) return 0;
        if (now >= d2) return 100;
        return Math.round(((now - d1) / (d2 - d1)) * 100);
    };

    /* --------- RÉMUNÉRATION --------- */
    const remunerationData = useMemo(() => {
        if (!contrat?.remuneration_json) return null;
        try {
            return typeof contrat.remuneration_json === 'string'
                ? JSON.parse(contrat.remuneration_json)
                : contrat.remuneration_json;
        } catch { return null; }
    }, [contrat]);

    const totalRemuneration = useMemo(() => {
        if (!remunerationData) return 0;
        return Object.values(remunerationData).reduce((sum, v) => sum + (Number(v) || 0), 0);
    }, [remunerationData]);

    /* --------- MISSIONS --------- */
    const missions = useMemo(() => {
        if (!contrat?.poste_missions) return [];
        try {
            return typeof contrat.poste_missions === 'string'
                ? JSON.parse(contrat.poste_missions)
                : contrat.poste_missions;
        } catch { return []; }
    }, [contrat]);

    /* --------- CLAUSES --------- */
    const clausesData = useMemo(() => {
        if (!contrat?.clauses_json) return null;
        try {
            return typeof contrat.clauses_json === 'string'
                ? JSON.parse(contrat.clauses_json)
                : contrat.clauses_json;
        } catch { return null; }
    }, [contrat]);

    /* ============================================================
       GÉNÉRATION HTML IMPRESSION
       ============================================================ */
    const genererHTMLImpression = () => {
        if (!contrat) return '';

        const remun = remunerationData || {};
        const missionsList = missions || [];
        const typeDoc = contrat.type_contrat || 'CDD';

        const fcfa = (n) => (Number(n) || 0).toLocaleString('fr-FR');
        const dateFr = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
        const dateLong = (d) => {
            if (!d) return '—';
            return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
        };
        const civilite = (sexe) => sexe === 'F' ? 'Madame' : 'Monsieur';

        const employe = {
            nom: contrat.employe_nom || contrat.employe_nom_ref || '',
            prenom: contrat.employe_prenom || contrat.employe_prenom_ref || '',
            nomComplet: `${contrat.employe_prenom || contrat.employe_prenom_ref || ''} ${contrat.employe_nom || contrat.employe_nom_ref || ''}`.trim(),
            dateNaissance: contrat.employe_date_naissance || null,
            lieuNaissance: contrat.employe_lieu_naissance || '',
            situationFamiliale: contrat.employe_situation_matrimoniale || 'Célibataire',
            nationalite: contrat.employe_nationalite || 'Gabonaise',
            nombreEnfants: contrat.employe_nombre_enfants ?? 0,
            sexe: contrat.employe_sexe || 'M',
            cnss: contrat.employe_cnss || '',
            pieceIdentite: contrat.employe_piece_identite || '',
            domicile: contrat.employe_domicile || contrat.employe_adresse || contrat.employe_adresse_ref || '',
            nomPere: contrat.employe_nom_pere || '',
            nomMere: contrat.employe_nom_mere || '',
            personnePrevenir: contrat.employe_personne_prevenir || '',
            telPrevenir: contrat.employe_tel_prevenir || '',
            telPersonnel: contrat.employe_telephone || contrat.employe_telephone_ref || '',
            email: contrat.employe_email || contrat.employe_email_ref || '',
            dateEntree: contrat.employe_date_embauche || contrat.date_debut
        };

        const employeur = {
            nom: contrat.employeur_nom || 'CONSULTING MAXI SERVICES',
            adresse: contrat.employeur_adresse || 'B.P. 548 - Port-Gentil',
            telephone: contrat.employeur_telephone || '01 56 09 42 / 07 35 48 80',
            email: contrat.employeur_email || 'cmsmangalaeliane@yahoo.fr',
            representant: 'Mme MABICKA Orphée Lorraine Epse MAKAKOU',
            qualite: 'Gérante',
            rccm: 'RG/POG 2010B 0582',
            nif: '799 747 M',
            cnss: '001 0028 603-M',
            cnamgs: '041500000733',
            ascoma: '8CP0400111'
        };

        const clientNom = contrat.client_raison_sociale || 'PERENCO OIL & GAS GABON';

        const baseCSS = `
            @page { size: A4; margin: 12mm 15mm; }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
                font-family: 'Times New Roman', 'Georgia', serif;
                color: #000;
                font-size: 10.5pt;
                line-height: 1.45;
                background: white;
            }
            .page { max-width: 210mm; margin: 0 auto; padding: 0; }
            .entete { text-align: center; padding-bottom: 12px; border-bottom: 2px solid #295AA7; margin-bottom: 18px; }
            .entete-logo { height: 55px; margin-bottom: 6px; }
            .entete-nom { font-size: 12pt; font-weight: bold; color: #1e3f7a; text-transform: uppercase; letter-spacing: 0.5px; }
            .entete-activite { font-size: 8pt; color: #333; font-style: italic; margin: 3px 0; }
            .entete-contact { font-size: 8pt; color: #333; margin-top: 3px; }
            .titre-doc { font-size: 13pt; font-weight: bold; text-align: center; text-transform: uppercase; text-decoration: underline; margin: 18px 0 15px 0; letter-spacing: 0.5px; }
            .sous-titre { font-size: 10pt; text-align: center; font-style: italic; margin-bottom: 15px; }
            .paragraphe { text-align: justify; margin-bottom: 10px; font-size: 10pt; line-height: 1.5; }
            .parties-intro { margin-bottom: 12px; font-size: 10pt; text-align: justify; line-height: 1.5; }
            .parties-bloc { margin: 10px 0; padding-left: 15px; }
            .parties-bloc p { margin-bottom: 3px; font-size: 10pt; }
            .titre-section { font-size: 11pt; font-weight: bold; text-transform: uppercase; margin: 16px 0 8px 0; text-decoration: underline; }
            .sous-section { font-size: 10.5pt; font-weight: bold; margin: 10px 0 6px 0; }
            .liste { padding-left: 25px; margin: 6px 0 10px 0; }
            .liste li { margin-bottom: 3px; font-size: 10pt; }
            .signature-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; margin-top: 35px; page-break-inside: avoid; }
            .signature-bloc { text-align: center; }
            .signature-titre { font-size: 10pt; font-weight: bold; margin-bottom: 45px; }
            .signature-ligne { border-bottom: 1px solid #000; height: 0; margin: 0 10px; }
            .signature-nom { font-size: 9pt; font-weight: bold; margin-top: 5px; }
            .signature-qualite { font-size: 8.5pt; font-style: italic; color: #333; }
            .pied-page { margin-top: 20px; padding-top: 8px; border-top: 1px solid #999; text-align: center; font-size: 7.5pt; color: #666; }
            .reference { text-align: center; font-size: 10pt; font-weight: bold; margin: 10px 0; }
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        `;

        /* TEMPLATE 1 : CONTRAT CDD / CDI */
        const templateContrat = () => {
            const isCDD = typeDoc === 'CDD' || (contrat.date_fin && typeDoc !== 'CDI');
            const titre = isCDD
                ? 'CONTRAT DE TRAVAIL A DUREE DETERMINEE'
                : 'CONTRAT DE TRAVAIL A DUREE INDETERMINEE';

            return `
            <div class="page">
                <div class="entete">
                    ${logoBase64 ? `<img src="${logoBase64}" class="entete-logo" alt="Logo"/>` : ''}
                    <div class="entete-nom">C.M.S — CONSULTING MAXI SERVICES</div>
                    <div class="entete-activite">Prestation de services Métallurgie, Soudure, Tuyauterie & Chaudronnerie — Manutention Maritimes & Génie Civil</div>
                    <div class="entete-contact">
                        B.P. 548 — Tél : 01 56 09 42 — Cel : 07 35 48 80 — Port-Gentil<br/>
                        RCCM : ${employeur.rccm} — NIF : ${employeur.nif} — N° CNSS : ${employeur.cnss} — N° CNAMGS : ${employeur.cnamgs}
                    </div>
                </div>

                <div class="titre-doc">${titre}</div>
                ${contrat.reference ? `<div class="reference">Réf. : ${contrat.reference}</div>` : ''}

                <p class="parties-intro"><strong>Entre les soussignés :</strong></p>
                <div class="parties-bloc">
                    <p><strong>C.M.S &lt;&lt;Consulting Maxi Services&gt;&gt;</strong> Société à Responsabilité Limitée dont le siège social est basé à Port-Gentil, B.P. 548, Tél : 01 56 09 42, Cel : 07 35 48 80, représentée par <strong>${employeur.representant}</strong>, agissant en qualité de <strong>${employeur.qualite}</strong>.</p>
                    <p>Conformément aux dispositions de l'article 23 du Code du Travail en vigueur en République Gabonaise.</p>
                    <p style="margin-top: 6px;">Ci-après dénommé <strong>&lt;&lt; l'Employeur &gt;&gt;</strong>, d'une part ;</p>
                </div>

                <p class="parties-intro" style="margin-top: 10px;"><strong>Et</strong></p>

                <div class="parties-bloc">
                    <p><strong>${civilite(contrat.employe_sexe)} ${employe.nomComplet}</strong></p>
                    <p>Né${contrat.employe_sexe === 'F' ? 'e' : ''} le : <strong>${dateLong(employe.dateNaissance)}</strong> à <strong>${employe.lieuNaissance || '—'}</strong></p>
                    <p>Situation familiale : <strong>${employe.situationFamiliale}</strong></p>
                    <p>Nationalité : <strong>${employe.nationalite}</strong></p>
                    <p>Nombre d'enfants : <strong>${employe.nombreEnfants}</strong></p>
                    ${employe.cnss ? `<p>N° CNSS : <strong>${employe.cnss}</strong></p>` : ''}
                    ${employe.pieceIdentite ? `<p>Pièce d'identité : <strong>${employe.pieceIdentite}</strong></p>` : ''}
                    <p>Domicilié : <strong>${employe.domicile || '—'}</strong></p>
                    ${employe.nomPere ? `<p>Nom du père : <strong>${employe.nomPere}</strong></p>` : ''}
                    ${employe.nomMere ? `<p>Nom de la Mère : <strong>${employe.nomMere}</strong></p>` : ''}
                    ${employe.personnePrevenir ? `<p>Personne à prévenir en cas de besoin : <strong>${employe.personnePrevenir}</strong>${employe.telPrevenir ? ` — Tél : ${employe.telPrevenir}` : ''}</p>` : ''}
                    ${employe.telPersonnel ? `<p>Tel personnel : <strong>${employe.telPersonnel}</strong></p>` : ''}
                    <p style="margin-top: 6px;">Ci-après désigné(e) <strong>&lt;&lt; Le Travailleur &gt;&gt;</strong>, d'autre part.</p>
                </div>

                <p class="paragraphe" style="text-align: center; font-weight: bold; margin-top: 15px;">
                    IL A ETE CONVENU CE QUI SUIT :
                </p>

                <div class="titre-section">Titre 1 : Du contrat de travail</div>
                <p class="paragraphe">Le présent contrat est régi par les textes suivants :</p>
                <ul class="liste">
                    <li>Loi N°3/9 du 21 Novembre 1994, modifiée par la loi N°12/2000 du 12 Octobre 2000, portant Code du Travail de la République Gabonaise ;</li>
                    <li>Observation de toutes dispositions en législation du travail et application de celle-ci ;</li>
                    <li>Décret N°599/PR du 17 Juin 1981 fixant les modalités d'application du Code de la Sécurité Sociale ; Loi N°6/75 du 25 Novembre 1975 ;</li>
                    <li>Le contrat de gestion signé entre Consulting Maxi Services (C.M.S) et son client <strong>${clientNom}</strong> ;</li>
                    <li>Avenants et décisions qui sont venus ou viendraient compléter ou modifier la convention ou l'arrêté précité.</li>
                </ul>

                <div class="sous-section">1.1 — De la qualification professionnelle</div>
                <p class="paragraphe">L'employé occupera le poste de <strong>${contrat.poste_qualification || contrat.poste_fonction || 'Manutentionnaire'}</strong>.</p>
                <p class="paragraphe">Sa mission consistera à exécuter :</p>
                <ul class="liste">
                    ${missionsList.length > 0
                        ? missionsList.map(m => `<li>${m}</li>`).join('')
                        : `
                            <li>Tous les travaux se rapportant aux tâches de ${contrat.poste_qualification || 'Manutentionnaire'}</li>
                            <li>Assister les collègues dans l'exécution des tâches assimilées</li>
                        `}
                </ul>
                <p class="paragraphe">Pendant toute la durée de l'exécution, l'employé est soumis au règlement intérieur du client.</p>

                <div class="titre-section">Titre 2 : Durée et lieu d'exécution du contrat</div>
                <div class="sous-section">2.1 — Durée du contrat</div>
                ${isCDD ? `
                    <p class="paragraphe">
                        Le présent contrat est conclu pour une durée de <strong>${contrat.jours_site ? (contrat.jours_site + ' jours') : ''}${contrat.jours_sortie ? ` (+ ${contrat.jours_sortie} jour${contrat.jours_sortie > 1 ? 's' : ''} pour la sortie)` : ''}</strong>,
                        allant du <strong>${dateFr(contrat.date_debut)}</strong> au <strong>${dateFr(contrat.date_fin)}</strong>.
                        Cependant, le contrat peut s'interrompre avant terme en cas de fin de chantier.
                        De ce fait, le nombre de jours à payer correspondra aux jours de présence sur site (selon pointage).
                    </p>
                ` : `
                    <p class="paragraphe">
                        Le présent contrat est conclu pour une durée indéterminée à compter du <strong>${dateFr(contrat.date_debut)}</strong>.
                    </p>
                `}
                <p class="paragraphe">L'employé s'engage à ne quitter son poste que sur autorisation préalable de sa hiérarchie ou aux heures prévues par le Code du Travail Gabonais.</p>
                <p class="paragraphe">L'interruption partielle ou définitive de l'activité par le client conduira à la suspension de ce contrat.</p>

                <div class="titre-section">Titre 3 : Lieu de l'emploi</div>
                <p class="paragraphe">
                    Le travailleur est engagé pour servir dans la province de <strong>${contrat.poste_province || 'l\'Ogooué Maritime (Port-Gentil)'}</strong>
                    et en tout lieu de la République Gabonaise où l'employeur aurait besoin de ses services, notamment sur le site : <strong>${contrat.poste_site || 'BATANGA'}</strong>.
                </p>
                <p class="paragraphe">
                    En effet, tout refus de ce dernier de se soumettre à l'application desdites dispositions entraînera la rupture dudit contrat pour faute lourde avec conséquence de licenciement.
                </p>
                <p class="paragraphe">
                    Contrat de gestion signé entre Consulting Maxi Services (C.M.S) et son client <strong>${clientNom}</strong>.
                </p>

                <div class="sous-section">3.1 — De l'emploi</div>
                <p class="paragraphe">
                    Le travailleur est embauché au poste de : <strong>${contrat.poste_qualification || 'Manutentionnaire'}</strong>.<br/>
                    L'emploi ci-dessus défini implique le classement du travailleur en : Fonction : <strong>${contrat.poste_fonction || 'Manutentionnaire'}</strong>.
                    ${contrat.poste_departement ? `<br/>Département : <strong>${contrat.poste_departement}</strong>` : ''}
                </p>

                <div class="titre-section">Titre 4 : De la rémunération</div>
                <div class="sous-section">4.1 — Rémunération sur site</div>
                <p class="paragraphe">La rémunération du travail sur site est composée de ce qui suit :</p>
                <ul class="liste">
                    ${remun.salaire_base ? `<li>Salaire de base : <strong>${fcfa(remun.salaire_base)} F CFA / 28 Jours</strong></li>` : ''}
                    ${remun.prime_assiduite ? `<li>Prime d'Assiduité : <strong>${fcfa(remun.prime_assiduite)} F CFA / M</strong></li>` : ''}
                    ${remun.prime_securite ? `<li>Prime de Sécurité : <strong>${fcfa(remun.prime_securite)} F CFA / M</strong></li>` : ''}
                    ${remun.prime_chantier ? `<li>Prime de Chantier : <strong>${fcfa(remun.prime_chantier)} F CFA</strong></li>` : ''}
                    ${remun.prime_mer ? `<li>Prime de Mer : <strong>${fcfa(remun.prime_mer)} F CFA / M</strong></li>` : ''}
                    ${remun.prime_rendement ? `<li>Prime de Rendement : <strong>${fcfa(remun.prime_rendement)} F CFA / M</strong></li>` : ''}
                    ${remun.prime_gaz ? `<li>Prime de Gaz : <strong>${fcfa(remun.prime_gaz)} F CFA / M</strong></li>` : ''}
                    ${remun.prime_transport ? `<li>Prime de transport : <strong>${fcfa(remun.prime_transport)} F CFA / M</strong></li>` : ''}
                    ${remun.indemnite_diverse ? `<li>Indemnité diverse : <strong>${fcfa(remun.indemnite_diverse)} F CFA / M</strong></li>` : ''}
                </ul>

                <p class="paragraphe">Sont considérées comme heures supplémentaires les heures travaillées au-delà de la douzième (12) heure de travail, c'est-à-dire à partir de la treizième (13) heure.</p>

                <div class="sous-section">4.2 — Rémunération récupération</div>
                <ul class="liste">
                    ${remun.salaire_base ? `<li>Salaire de base : <strong>${fcfa(remun.salaire_base)} FCFA</strong></li>` : ''}
                    ${remun.prime_gaz ? `<li>Prime de gaz : <strong>${fcfa(remun.prime_gaz)} FCFA / M</strong></li>` : ''}
                    ${remun.indemnite_diverse ? `<li>Indemnité diverse : <strong>${fcfa(remun.indemnite_diverse)} FCFA / M</strong></li>` : ''}
                </ul>
                <p class="paragraphe"><em>NB : Cette indemnité diverse sera payée au prorata des jours travaillés.</em></p>
                <p class="paragraphe">Prime d'ancienneté : selon la législation en vigueur.</p>

                <div class="titre-section">Titre 5 : Pénalités préventives</div>
                <p class="paragraphe">
                    Tout employé qui, de son propre gré, prendra des dispositions contraires audit contrat de travail signé entre C.M.S et l'employé,
                    afin d'enfreindre les dispositions qui lient le client et la société C.M.S au regard des dispositions relatives au Contrat Cadre
                    N° Réf : POGG-CMS-MSA-1117-1, entre C.M.S et son Client, se verra appliquer toutes pénalités de paiement financier que le client
                    aura exigé par faute dudit employé.
                </p>
                <p class="paragraphe">
                    Notamment : l'absence de l'agent à l'embarquement, retard à l'heure de l'embarquement, équipement non-conforme au cas où il est
                    question d'une responsabilité de l'employé et tous frètements des matériels ou marchandises qui ne relèvent pas de la mission
                    pour laquelle l'on a été engagé à son contrat de travail.
                </p>
                <p class="paragraphe">
                    Toutefois, la pénalité sera déduite en fonction du montant que le client aura extirpé sur la facture CMS, si et seulement si cette
                    pénalité est déduite par la faute de l'employé suite à la non observation dudit contrat qui nous lie employé et employeur.
                </p>

                <div class="titre-section">Titre 6 : Congés payés</div>
                <p class="paragraphe">
                    Le travailleur bénéficiera des congés dans les conditions fixées par la loi et la convention collective susmentionnée.
                </p>

                <div class="titre-section">Titre 7 : Fautes professionnelles</div>
                <p class="paragraphe">
                    Le travailleur s'engage à accepter que les fautes professionnelles éditées ci-dessous, entre autres, soient passibles de
                    licenciement sans préavis ni indemnité :
                </p>
                <ol class="liste">
                    <li>Insuffisance professionnelle ;</li>
                    <li>Inadaptation en milieu du travail ;</li>
                    <li>Usage de faux, Contrebande, Trucage des données & Matériels ;</li>
                    <li>Indisponibilité & Absence non justifiée ;</li>
                    <li>Non-respect scrupuleux des règles d'hygiène ;</li>
                    <li>S'abstenir à l'absorption d'alcool, le vol, refus de travail, l'indiscipline caractérisée ;</li>
                    <li>La rixe, violence et voies de fait à l'endroit d'un collègue ou de ses chefs hiérarchiques, trouble sur le chantier.</li>
                </ol>

                <div class="titre-section">Titre 8 : Disposition particulière</div>
                <ul class="liste">
                    <li>L'employé s'engage sur la base de ce présent contrat, en toute âme et conscience, de toutes les dispositions conclues entre la société C.M.S et lui, tout en estimant être d'accord sur les éléments portés au contrat ;</li>
                    <li>La société C.M.S se réserve le droit de ne pas renouveler les contrats des employés qui démontreraient une attitude à même de mettre en danger les intérêts du client ou de celle-ci ;</li>
                    <li>En cas de remplacement pour une courte durée, le calcul du salaire se fera au prorata des jours travaillés ;</li>
                    <li>L'employé s'engage sans équivoque après avoir pris connaissance des dispositions liées à cet engagement, tout en respectant les clauses portées audit contrat de mission ;</li>
                    <li>Aussi, en cas de contentieux, ces dispositions pourraient être prises en compte afin de défendre les intérêts du client et de la société de mise à disposition.</li>
                </ul>

                <div class="titre-section">Soins médicaux</div>
                <p class="paragraphe">
                    Le travailleur sera astreint, avant son embauche, à une visite spécifiant ses capacités à exercer l'emploi pour lequel il a été recruté.
                </p>

                <div class="titre-section">Clause de non-concurrence</div>
                <p class="paragraphe">
                    Reprenant l'article 34 du Code du Travail en vigueur en République Gabonaise, le travailleur déclare être libre de tout engagement
                    et donner son libre consentement au présent contrat de travail.
                </p>

                <p class="paragraphe" style="margin-top: 15px;">
                    Fait à <strong>Port-Gentil</strong>, le <strong>${dateLong(new Date())}</strong>.
                </p>

                <div class="signature-grid">
                    <div class="signature-bloc">
                        <div class="signature-titre">L'Employé</div>
                        <div class="signature-ligne"></div>
                        <div class="signature-nom">${employe.nomComplet}</div>
                    </div>
                    <div class="signature-bloc">
                        <div class="signature-titre">Chargée du personnel</div>
                        <div class="signature-ligne"></div>
                        <div class="signature-nom">Mme KOUMBA</div>
                    </div>
                </div>

                <p class="paragraphe" style="margin-top: 20px; font-size: 9pt; font-style: italic;">
                    <strong>NB :</strong> Fournir deux photos d'identité couleurs et une copie d'acte de naissance légalisée ou une copie de la carte CNSS
                    pour les assurés, une pièce d'état civil légalisée auprès de votre ambassade pour les étrangers. Se rapprocher de la Direction de C.M.S
                    pour avoir plus ample information sur l'affiliation à la CNAMGS, toutefois il est obligatoire à tout employé d'être affilié.
                </p>

                <div class="pied-page">
                    C.M.S — Consulting Maxi Services — B.P. 548 Port-Gentil — Tél : 01 56 09 42 — Cel : 07 35 48 80
                </div>
            </div>
            `;
        };

        /* TEMPLATE 2 : AVENANT */
        const templateAvenant = () => {
            return `
            <div class="page">
                <div class="entete">
                    ${logoBase64 ? `<img src="${logoBase64}" class="entete-logo" alt="Logo"/>` : ''}
                    <div class="entete-nom">C.M.S — CONSULTING MAXI SERVICES</div>
                    <div class="entete-activite">Prestation de services Métallurgie, Soudure, Tuyauterie & Chaudronnerie</div>
                    <div class="entete-contact">
                        B.P. 548 — Tél. / Fax : 01.56.09.42 — Cel : 07.35.48.80 — Port-Gentil
                    </div>
                </div>

                <div class="titre-doc">
                    AVENANT ${contrat.contrat_initial_ref ? `N° ${contrat.contrat_initial_ref}` : 'AU CONTRAT DE TRAVAIL A DUREE DETERMINEE'}
                </div>
                ${contrat.contrat_initial_dates ? `<div class="reference">${contrat.contrat_initial_dates}</div>` : ''}

                <p class="parties-intro"><strong>Entre les soussignés :</strong></p>
                <div class="parties-bloc">
                    <p><strong>C.M.S &lt;&lt;Consulting Maxi Services&gt;&gt;</strong> Société à Responsabilité Limitée dont le siège social est basé à Port-Gentil, B.P. 548, Tél : 01 56 09 42, Cel : 07 35 48 80, représentée par <strong>${employeur.representant}</strong>, agissant en qualité de <strong>${employeur.qualite}</strong>.</p>
                    <p>Conformément aux dispositions de l'article 23 du Code du Travail en vigueur en République Gabonaise.</p>
                    <p>Ci-après dénommé <strong>&lt;&lt; l'Employeur &gt;&gt;</strong>, d'une part ;</p>
                </div>

                <p class="parties-intro" style="margin-top: 10px;"><strong>Et</strong></p>

                <div class="parties-bloc">
                    <p><strong>${civilite(contrat.employe_sexe)} ${employe.nomComplet}</strong></p>
                    <p>Né${contrat.employe_sexe === 'F' ? 'e' : ''} le : <strong>${dateLong(employe.dateNaissance)}</strong> à <strong>${employe.lieuNaissance || '—'}</strong></p>
                    <p>Situation familiale : <strong>${employe.situationFamiliale}</strong></p>
                    <p>Nationalité : <strong>${employe.nationalite}</strong></p>
                    <p>Nombre d'enfants : <strong>${employe.nombreEnfants}</strong></p>
                    ${employe.cnss ? `<p>N° CNSS : <strong>${employe.cnss}</strong></p>` : ''}
                    <p>Domicilié : <strong>${employe.domicile || '—'}</strong></p>
                    ${employe.nomPere ? `<p>Nom du père : <strong>${employe.nomPere}</strong></p>` : ''}
                    ${employe.nomMere ? `<p>Nom de la Mère : <strong>${employe.nomMere}</strong></p>` : ''}
                    ${employe.personnePrevenir ? `<p>Personne à prévenir en cas de besoin : <strong>${employe.personnePrevenir}</strong>${employe.telPrevenir ? ` — Tél : ${employe.telPrevenir}` : ''}</p>` : ''}
                    <p style="margin-top: 6px;">Ci-après désigné(e) <strong>&lt;&lt; Le Travailleur &gt;&gt;</strong>, d'autre part.</p>
                </div>

                <p class="paragraphe" style="text-align: center; font-weight: bold; margin-top: 15px;">
                    IL A ETE CONVENU CE QUI SUIT :
                </p>

                <div class="titre-section">Préambule</div>
                <p class="paragraphe">
                    ${civilite(contrat.employe_sexe)} <strong>${employe.nomComplet}</strong> a été embauché${contrat.employe_sexe === 'F' ? 'e' : ''}
                    le <strong>${dateLong(employe.dateEntree)}</strong> en contrat à durée déterminée (CDD)
                    ${contrat.contrat_initial_dates ? `(${contrat.contrat_initial_dates})` : ''}.
                    Ce contrat avait pour objet d'assurer les tâches de <strong>${contrat.poste_qualification || 'Manutentionnaire'}</strong>
                    au Département <strong>${contrat.poste_departement || clientNom}</strong>.
                </p>
                <p class="paragraphe">
                    Constatant un besoin d'intervention sur le site <strong>${contrat.poste_site || 'BATANGA'}</strong>,
                    les parties ont décidé de renouveler le contrat qui les liait et de fixer, par le présent AVENANT,
                    la durée et les modalités de ce renouvellement.
                </p>

                <div class="titre-section">Article 1 : Renouvellement</div>
                <p class="paragraphe">
                    Le contrat à durée déterminée conclu entre les parties
                    ${contrat.contrat_initial_dates ? `(${contrat.contrat_initial_dates})` : ''}
                    est renouvelé pour une durée de
                    <strong>${contrat.jours_site ? (contrat.jours_site + ' jours') : ''}${contrat.jours_sortie ? ` (+ ${contrat.jours_sortie} jour${contrat.jours_sortie > 1 ? 's' : ''} pour la sortie)` : ''}</strong>
                    allant du <strong>${dateFr(contrat.date_debut)}</strong> au <strong>${dateFr(contrat.date_fin)}</strong>.
                </p>

                <div class="titre-section">Article 2 : Dispositions diverses</div>
                <p class="paragraphe">
                    Les autres clauses du contrat demeurent inchangées.
                </p>

                <p class="paragraphe" style="margin-top: 20px;">
                    Fait en double original, à <strong>Port-Gentil</strong>, le <strong>${dateLong(new Date())}</strong>.
                </p>

                <div class="signature-grid">
                    <div class="signature-bloc">
                        <div class="signature-titre">L'Employé</div>
                        <div class="signature-ligne"></div>
                        <div class="signature-nom">${employe.nomComplet}</div>
                    </div>
                    <div class="signature-bloc">
                        <div className="signature-titre">Chargée du personnel</div>
                        <div class="signature-ligne"></div>
                        <div class="signature-nom">Mme KOUMBA</div>
                    </div>
                </div>

                <div class="pied-page">
                    C.M.S — Consulting Maxi Services — B.P. 548 Port-Gentil — Tél : 01 56 09 42 — Cel : 07 35 48 80
                </div>
            </div>
            `;
        };

        /* TEMPLATE 3 : CERTIFICAT DE TRAVAIL */
        const templateCertificat = () => {
            return `
            <div class="page">
                <div class="entete">
                    ${logoBase64 ? `<img src="${logoBase64}" class="entete-logo" alt="Logo"/>` : ''}
                    <div class="entete-nom">C.M.S — CONSULTING MAXI SERVICES</div>
                    <div class="entete-activite">Prestation de services Métallurgiques — Manutentionnaires, Maritimes et Génie Civil</div>
                    <div class="entete-contact">
                        B.P. 548 — Tél. 01 56 09 42 — Cel : 07 35 48 80 / 05 05 37 29 — Port-Gentil
                    </div>
                </div>

                <div class="titre-doc">CERTIFICAT DE TRAVAIL</div>

                <p class="paragraphe" style="margin-top: 25px; font-size: 11pt;">
                    Nous soussignés, <strong>CONSULTING MAXI SERVICES</strong>, certifions par le présent Certificat de Travail que
                    ${civilite(contrat.employe_sexe)} <strong>${employe.nomComplet}</strong>
                    a été employé${contrat.employe_sexe === 'F' ? 'e' : ''} dans notre Société en qualité de
                    <strong>${contrat.poste_qualification || 'Manutentionnaire'}</strong>,
                    pour le Département <strong>${contrat.poste_departement || `C.M.S / ${clientNom}`}</strong>.
                </p>

                <p class="paragraphe" style="margin-top: 15px; font-size: 11pt;">
                    Du : <strong>${dateLong(contrat.date_debut)}</strong>
                </p>
                <p class="paragraphe" style="font-size: 11pt;">
                    Au : <strong>${dateLong(contrat.date_fin)}</strong>
                </p>

                <p class="paragraphe" style="margin-top: 25px; font-size: 11pt;">
                    L'intéressé${contrat.employe_sexe === 'F' ? 'e' : ''} nous quitte ce jour libre de tout engagement, en foi de quoi nous lui délivrons
                    le présent Certificat de Travail pour servir et valoir ce que de droit.
                </p>

                <p class="paragraphe" style="margin-top: 30px; font-size: 11pt;">
                    Fait à <strong>Port-Gentil</strong>, le <strong>${dateLong(new Date())}</strong>
                </p>

                <div style="text-align: right; margin-top: 40px; padding-right: 30px;">
                    <div style="font-size: 11pt; font-weight: bold; margin-bottom: 40px;">Service des Ressources Humaines</div>
                    <div style="border-bottom: 1px solid #000; width: 200px; margin-left: auto;"></div>
                    <div style="font-size: 10pt; font-weight: bold; margin-top: 5px; text-align: center; width: 200px; margin-left: auto;">Mme KOUMBA</div>
                </div>

                <div class="pied-page">
                    C.M.S — Consulting Maxi Services — B.P. 548 Port-Gentil — Tél. 01 56 09 42 — Cel 07 35 48 80
                </div>
            </div>
            `;
        };

        /* ASSEMBLAGE FINAL */
        let corps = '';
        if (typeDoc === 'AVENANT') {
            corps = templateAvenant();
        } else if (typeDoc === 'CERTIFICAT') {
            corps = templateCertificat();
        } else {
            corps = templateContrat();
        }

        return `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <title>${typeDoc} - ${employe.nomComplet}</title>
    <style>${baseCSS}</style>
</head>
<body>
    ${corps}
</body>
</html>
        `;
    };

    const handlePrint = () => {
        const html = genererHTMLImpression();
        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'position:absolute;width:0;height:0;border:none;';
        document.body.appendChild(iframe);
        const doc = iframe.contentWindow.document;
        doc.open(); doc.write(html); doc.close();
        iframe.contentWindow.onload = () => {
            iframe.contentWindow.print();
            setTimeout(() => { if (document.body.contains(iframe)) document.body.removeChild(iframe); }, 1000);
        };
        showToast('info', 'Impression', 'Document envoyé à l\'impression');
    };

    /* --------- AFFICHAGE --------- */
    const ActionButton = ({ icon, onClick, tooltip, color, disabled }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
            <button
                className="detail-action-btn"
                onClick={onClick}
                disabled={disabled}
                style={{ '--btn-color': color || COLORS.primary }}
            >
                <i className={icon}></i>
            </button>
        </OverlayTrigger>
    );

    if (loading && !contrat) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center" style={{ maxWidth: '400px' }}>
                            <div className="loading-ring-wrapper mb-4">
                                <div className="loading-ring"></div>
                                <div className="loading-ring-inner"></div>
                                <i className="ri-loader-4-fill loading-icon"></i>
                            </div>
                            <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                Chargement du contrat...
                            </h5>
                            <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                            <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                    <div
                                        className="progress-bar progress-bar-striped progress-bar-animated"
                                        style={{
                                            width: '100%',
                                            background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                            backgroundSize: '200% 100%',
                                            animation: 'shimmer 1.5s linear infinite'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                    .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                    @keyframes spin { to { transform: rotate(360deg); } }
                    @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                `}</style>
            </>
        );
    }

    if (error || !contrat) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error || 'Contrat non trouvé'}</p>
                            </div>
                            <Button onClick={() => navigate('/rh/contrats')} className="btn-modern-outline">
                                <i className="ri-arrow-left-line me-1"></i>Retour
                            </Button>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    const statut = getStatutBadge();
    const joursRestants = getJoursRestants();
    const progress = getProgressPeriod();

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">

                    <ToastNotification show={toast.show} onClose={hideToast} type={toast.type} title={toast.title} message={toast.message} />

                    {/* ===== EN-TÊTE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-file-copy-2-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Détail du contrat</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-user-line"></i>
                                        {contrat.employe_prenom} {contrat.employe_nom}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                        <i className="ri-file-list-3-line"></i>
                                        {contrat.type_contrat || 'CDD'}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                        <i className="ri-hashtag"></i>
                                        {contrat.reference || `#${contrat.id}`}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={() => navigate('/rh/contrats')}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                            <Button className="btn-modern-outline" onClick={handlePrint}>
                                <i className="ri-printer-line"></i> Imprimer
                            </Button>
                            <Button className="btn-modern-primary" onClick={() => setShowEditModal(true)}>
                                <i className="ri-edit-line"></i> Modifier
                            </Button>
                            <Button className="btn-modern-danger" onClick={() => setShowDeleteModal(true)}>
                                <i className="ri-delete-bin-line"></i> Supprimer
                            </Button>
                        </div>
                    </div>

                    {/* ===== CARTE STATUT ===== */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4 overflow-hidden">
                        <div className="detail-hero" style={{ background: statut.gradient }}>
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="hero-icon">
                                        <i className={statut.icon}></i>
                                    </div>
                                    <div>
                                        <div className="hero-label">Statut du contrat</div>
                                        <div className="hero-value">{statut.text}</div>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center gap-3">
                                    <div className="hero-meta">
                                        <div className="hero-label">Période</div>
                                        <div className="hero-value">
                                            {formatDate(contrat.date_debut)}
                                            {contrat.date_fin && ` → ${formatDate(contrat.date_fin)}`}
                                            {!contrat.date_fin && ' → CDI'}
                                        </div>
                                    </div>
                                    <div className="hero-meta">
                                        <div className="hero-label">Durée</div>
                                        <div className="hero-value">{getDureeContrat()}</div>
                                    </div>
                                    {joursRestants !== null && (
                                        <div className="hero-meta">
                                            <div className="hero-label">Jours restants</div>
                                            <div className="hero-value">
                                                {joursRestants > 0 ? `${joursRestants} j` : (joursRestants === 0 ? 'Aujourd\'hui' : 'Expiré')}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {contrat.date_fin && (
                            <Card.Body className="p-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <small className="text-muted">Progression de la période</small>
                                    <small className="fw-semibold" style={{ color: COLORS.primary }}>{progress}%</small>
                                </div>
                                <div className="progress detail-progress">
                                    <div
                                        className="progress-bar"
                                        style={{
                                            width: `${progress}%`,
                                            background: progress >= 100
                                                ? 'linear-gradient(90deg, #ef4444, #dc2626)'
                                                : progress >= 75
                                                ? 'linear-gradient(90deg, #f59e0b, #d97706)'
                                                : 'linear-gradient(90deg, #295AA7, #21ACCD)'
                                        }}
                                    />
                                </div>
                            </Card.Body>
                        )}
                    </Card>

                    <Row className="g-4">
                        {/* ===== COLONNE PRINCIPALE ===== */}
                        <Col lg={8}>

                            {/* Parties */}
                            <Card className="border-0 shadow-sm rounded-3 mb-4">
                                <Card.Header className="card-header-charte">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-group-line text-white"></i>
                                        <span className="fw-semibold text-white">Parties contractantes</span>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Row className="g-4">
                                        <Col md={6}>
                                            <div className="partie-card">
                                                <div className="partie-header">
                                                    <div className="partie-icon" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                                                        <i className="ri-building-line"></i>
                                                    </div>
                                                    <div className="partie-title-main">Employeur</div>
                                                </div>
                                                <div className="partie-body">
                                                    <InfoRow icon="ri-building-2-line" label="Raison sociale" value={contrat.employeur_nom} color={COLORS.primary} />
                                                    {contrat.employeur_adresse && <InfoRow icon="ri-map-pin-line" label="Adresse" value={contrat.employeur_adresse} color={COLORS.primary} />}
                                                    {contrat.employeur_email && <InfoRow icon="ri-mail-line" label="Email" value={contrat.employeur_email} color={COLORS.primary} />}
                                                    {contrat.employeur_telephone && <InfoRow icon="ri-phone-line" label="Téléphone" value={contrat.employeur_telephone} color={COLORS.primary} />}
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="partie-card">
                                                <div className="partie-header">
                                                    <div className="partie-icon" style={{ background: `linear-gradient(135deg, ${COLORS.secondary}, ${COLORS.primaryDark})` }}>
                                                        <i className="ri-user-line"></i>
                                                    </div>
                                                    <div className="partie-title-main">Employé(e)</div>
                                                </div>
                                                <div className="partie-body">
                                                    <InfoRow icon="ri-user-line" label="Nom complet" value={`${contrat.employe_prenom} ${contrat.employe_nom}`} color={COLORS.secondary} />
                                                    {contrat.employe_adresse && <InfoRow icon="ri-map-pin-line" label="Adresse" value={contrat.employe_adresse} color={COLORS.secondary} />}
                                                    {contrat.employe_email && <InfoRow icon="ri-mail-line" label="Email" value={contrat.employe_email} color={COLORS.secondary} />}
                                                    {contrat.employe_telephone && <InfoRow icon="ri-phone-line" label="Téléphone" value={contrat.employe_telephone} color={COLORS.secondary} />}
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>

                                    {contrat.client_raison_sociale && (
                                        <div className="mt-4">
                                            <div className="client-highlight">
                                                <div className="client-highlight-icon">
                                                    <i className="ri-building-4-line"></i>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="client-highlight-label">Client / Donneur d'ordre</div>
                                                    <div className="client-highlight-value">{contrat.client_raison_sociale}</div>
                                                    {contrat.code_client && (
                                                        <div className="small text-muted mt-1">
                                                            <i className="ri-hashtag"></i> {contrat.code_client}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="client-highlight-badge">
                                                    <i className="ri-link"></i>
                                                    <span>ID #{contrat.id_client}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>

                            {/* Poste */}
                            <Card className="border-0 shadow-sm rounded-3 mb-4">
                                <Card.Header className="card-header-charte">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-briefcase-line text-white"></i>
                                        <span className="fw-semibold text-white">Poste & Affectation</span>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <InfoRow icon="ri-award-line" label="Qualification" value={contrat.poste_qualification} color={COLORS.primary} />
                                        </Col>
                                        <Col md={6}>
                                            <InfoRow icon="ri-user-settings-line" label="Fonction" value={contrat.poste_fonction} color={COLORS.primary} />
                                        </Col>
                                        <Col md={6}>
                                            <InfoRow icon="ri-node-tree" label="Département" value={contrat.poste_departement} color={COLORS.info} />
                                        </Col>
                                        <Col md={6}>
                                            <InfoRow icon="ri-map-pin-line" label="Site" value={contrat.poste_site} color={COLORS.info} />
                                        </Col>
                                        {contrat.poste_province && (
                                            <Col md={12}>
                                                <InfoRow icon="ri-map-2-line" label="Province" value={contrat.poste_province} color={COLORS.info} />
                                            </Col>
                                        )}
                                    </Row>

                                    {missions.length > 0 && (
                                        <div className="mt-4">
                                            <div className="section-title-inner">
                                                <span className="inner-icon" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                                    <i className="ri-list-check-2"></i>
                                                </span>
                                                <h6>Missions principales</h6>
                                            </div>
                                            <br></br>
                                            <div className="missions-list-wrapper">
                                                {missions.map((m, i) => (
                                                    <div key={i} className="mission-item">
                                                        <div className="mission-num">{i + 1}</div>
                                                        <div className="mission-text">{m}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>

                            {/* Rémunération */}
                            {remunerationData && Object.keys(remunerationData).length > 0 && (
                                <Card className="border-0 shadow-sm rounded-3 mb-4">
                                    <Card.Header className="card-header-charte">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className="d-flex align-items-center gap-2">
                                                <i className="ri-money-cfa-circle-line text-white"></i>
                                                <span className="fw-semibold text-white">Rémunération & Primes</span>
                                            </div>
                                            <Badge className="badge-header-light">
                                                {totalRemuneration.toLocaleString('fr-FR')} FCFA
                                            </Badge>
                                        </div>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <div className="remun-grid">
                                            {[
                                                { key: 'salaire_base',      label: 'Salaire de base',     icon: 'ri-money-cfa-circle-line' },
                                                { key: 'prime_assiduite',   label: 'Prime d\'assiduité',  icon: 'ri-calendar-check-line' },
                                                { key: 'prime_securite',    label: 'Prime de sécurité',   icon: 'ri-shield-check-line' },
                                                { key: 'prime_chantier',    label: 'Prime de chantier',   icon: 'ri-tools-line' },
                                                { key: 'prime_mer',         label: 'Prime de mer',        icon: 'ri-ship-line' },
                                                { key: 'prime_rendement',   label: 'Prime de rendement',  icon: 'ri-line-chart-line' },
                                                { key: 'prime_gaz',         label: 'Prime de gaz',        icon: 'ri-fire-line' },
                                                { key: 'prime_transport',   label: 'Prime de transport',  icon: 'ri-bus-line' },
                                                { key: 'indemnite_diverse', label: 'Indemnité diverse',   icon: 'ri-wallet-3-line' },
                                                { key: 'prime_anciennete',  label: 'Prime d\'ancienneté', icon: 'ri-time-line' }
                                            ]
                                                .filter(p => remunerationData[p.key] !== undefined)
                                                .map(p => (
                                                    <div key={p.key} className="remun-item">
                                                        <div className="remun-item-icon">
                                                            <i className={p.icon}></i>
                                                        </div>
                                                        <div className="flex-grow-1">
                                                            <div className="remun-item-label">{p.label}</div>
                                                            <div className="remun-item-value">
                                                                {(Number(remunerationData[p.key]) || 0).toLocaleString('fr-FR')} <span>FCFA</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                        <div className="total-remun-card mt-4">
                                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                                <div>
                                                    <div className="total-remun-label">Total mensuel estimé</div>
                                                    <div className="total-remun-sublabel">Somme de tous les éléments</div>
                                                </div>
                                                <div className="total-remun-value">
                                                    {totalRemuneration.toLocaleString('fr-FR')} <span>FCFA</span>
                                                </div>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            )}
                        </Col>

                        {/* ===== COLONNE LATÉRALE ===== */}
                        <Col lg={4}>

                            {/* Métadonnées */}
                            <Card className="border-0 shadow-sm rounded-3 mb-4">
                                <Card.Header className="card-header-charte">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-information-line text-white"></i>
                                        <span className="fw-semibold text-white">Informations</span>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <InfoRow icon="ri-hashtag" label="Référence" value={contrat.reference || `CT-${contrat.id}`} color={COLORS.primary} />
                                    <hr></hr>
                                    <InfoRow icon="ri-file-list-3-line" label="Type de contrat" value={contrat.type_contrat} color={COLORS.secondary} />
                                    <hr></hr>
                                    {contrat.contrat_initial_ref && (
                                        <>
                                            <InfoRow icon="ri-link" label="Contrat initial" value={contrat.contrat_initial_ref} color={COLORS.warning} />
                                            <hr></hr>
                                        </>
                                    )}
                                    <InfoRow icon="ri-calendar-check-line" label="Créé le" value={formatDate(contrat.created_at)} color={COLORS.info} />
                                    <hr></hr>
                                    {contrat.updated_at && contrat.updated_at !== contrat.created_at && (
                                        <InfoRow icon="ri-refresh-line" label="Modifié le" value={formatDate(contrat.updated_at)} color={COLORS.info} />
                                    )}
                                </Card.Body>
                            </Card>

                            {/* Documents */}
                            <Card className="border-0 shadow-sm rounded-3 mb-4">
                                <Card.Header className="card-header-charte">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-attachment-line text-white"></i>
                                        <span className="fw-semibold text-white">Documents</span>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    <div className="doc-item">
                                        <div className="doc-icon" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                            <i className="ri-file-pdf-line"></i>
                                        </div>
                                        <div className="flex-grow-1 min-width-0">
                                            <div className="doc-label">Lettre de fin de contrat</div>
                                            {contrat.lettre_fin_contrat ? (
                                                <div className="doc-filename">{contrat.lettre_fin_contrat.split('/').pop()}</div>
                                            ) : (
                                                <div className="doc-empty">Non remise</div>
                                            )}
                                        </div>
                                        {contrat.lettre_fin_contrat && (
                                            <button
                                                className="doc-download-btn"
                                                onClick={() => handleDownload('lettre_fin_contrat', contrat.lettre_fin_contrat.split('/').pop())}
                                            >
                                                <i className="ri-download-line"></i>
                                            </button>
                                        )}
                                    </div>

                                    <div className="doc-item">
                                        <div className="doc-icon" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                            <i className="ri-award-line"></i>
                                        </div>
                                        <div className="flex-grow-1 min-width-0">
                                            <div className="doc-label">Certificat de travail</div>
                                            {contrat.certificat_travail ? (
                                                <div className="doc-filename">{contrat.certificat_travail.split('/').pop()}</div>
                                            ) : (
                                                <div className="doc-empty">Non remis</div>
                                            )}
                                        </div>
                                        {contrat.certificat_travail && (
                                            <button
                                                className="doc-download-btn"
                                                onClick={() => handleDownload('certificat_travail', contrat.certificat_travail.split('/').pop())}
                                            >
                                                <i className="ri-download-line"></i>
                                            </button>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* Clauses */}
                            {clausesData && (
                                <Card className="border-0 shadow-sm rounded-3 mb-4">
                                    <Card.Header className="card-header-charte">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="ri-shield-check-line text-white"></i>
                                            <span className="fw-semibold text-white">Clauses</span>
                                        </div>
                                    </Card.Header>
                                    <Card.Body className="p-3">
                                        {clausesData.clause_non_concurrence && (
                                            <div className="clause-chip">
                                                <i className="ri-checkbox-circle-line"></i>
                                                <span>Non-concurrence</span>
                                            </div>
                                        )}
                                        {clausesData.soins_medicaux && (
                                            <div className="clause-chip">
                                                <i className="ri-checkbox-circle-line"></i>
                                                <span>Visite médicale</span>
                                            </div>
                                        )}
                                        {clausesData.penalites_preventives && (
                                            <div className="clause-chip">
                                                <i className="ri-checkbox-circle-line"></i>
                                                <span>Pénalités préventives</span>
                                            </div>
                                        )}
                                        {clausesData.observations && (
                                            <div className="mt-3">
                                                <div className="small text-muted mb-1">Observations</div>
                                                <div className="clause-obs">{clausesData.observations}</div>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            )}

                            {/* Actions rapides */}
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Body className="p-3">
                                    <Button className="btn-modern-primary w-100 mb-2" onClick={handlePrint}>
                                        <i className="ri-printer-line me-2"></i>Imprimer le contrat
                                    </Button>
                                    <Button className="btn-modern-outline w-100" onClick={() => setShowEditModal(true)}>
                                        <i className="ri-edit-line me-2"></i>Modifier
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            {/* ===== MODAL SUPPRESSION ===== */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte"
                    style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-delete-bin-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Supprimer le contrat</Modal.Title>
                            <p className="text-white-50 mb-0 small">Cette action est irréversible</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <p className="text-center text-muted mb-3">Êtes-vous sûr de vouloir supprimer ce contrat ?</p>
                    <div className="delete-summary-card">
                        <div className="d-flex align-items-center gap-3">
                            <div className="delete-avatar">
                                {contrat.employe_prenom?.charAt(0)}{contrat.employe_nom?.charAt(0)}
                            </div>
                            <div className="flex-grow-1">
                                <div className="fw-bold" style={{ color: COLORS.primaryDark }}>
                                    {contrat.employe_prenom} {contrat.employe_nom}
                                </div>
                                <div className="text-muted small">{contrat.employeur_nom}</div>
                                <div className="text-muted small">
                                    Du {formatDate(contrat.date_debut)}
                                    {contrat.date_fin && ` au ${formatDate(contrat.date_fin)}`}
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 px-4 pb-3">
                    <Button onClick={() => setShowDeleteModal(false)} className="btn-modern-outline" disabled={deleting}>
                        Annuler
                    </Button>
                    <Button onClick={handleDelete} disabled={deleting} className="btn-modern-danger">
                        {deleting ? <><Spinner size="sm" className="me-2" />Suppression...</> : <><i className="ri-delete-bin-line me-2"></i>Confirmer</>}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== MODAL MODIFICATION ===== */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte"
                    style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-edit-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Modifier le contrat</Modal.Title>
                            <p className="text-white-50 mb-0 small">Contrat #{contrat.id}</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <Form>
                        <div className="section-title-inner mb-3">
                            <span className="inner-icon" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                <i className="ri-building-line"></i>
                            </span>
                            <h6>Employeur</h6>
                        </div>
                        <Row className="g-3 mb-4">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Nom de l'entreprise</Form.Label>
                                    <Form.Control name="employeur_nom" value={editForm.employeur_nom || ''} onChange={handleEditChange} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Téléphone</Form.Label>
                                    <Form.Control name="employeur_telephone" value={editForm.employeur_telephone || ''} onChange={handleEditChange} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Email</Form.Label>
                                    <Form.Control type="email" name="employeur_email" value={editForm.employeur_email || ''} onChange={handleEditChange} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Adresse</Form.Label>
                                    <Form.Control name="employeur_adresse" value={editForm.employeur_adresse || ''} onChange={handleEditChange} />
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="section-title-inner mb-3">
                            <span className="inner-icon" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                <i className="ri-user-line"></i>
                            </span>
                            <h6>Employé</h6>
                        </div>
                        <Row className="g-3 mb-4">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Nom</Form.Label>
                                    <Form.Control name="employe_nom" value={editForm.employe_nom || ''} onChange={handleEditChange} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Prénom</Form.Label>
                                    <Form.Control name="employe_prenom" value={editForm.employe_prenom || ''} onChange={handleEditChange} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Email</Form.Label>
                                    <Form.Control type="email" name="employe_email" value={editForm.employe_email || ''} onChange={handleEditChange} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Téléphone</Form.Label>
                                    <Form.Control name="employe_telephone" value={editForm.employe_telephone || ''} onChange={handleEditChange} />
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Adresse</Form.Label>
                                    <Form.Control name="employe_adresse" value={editForm.employe_adresse || ''} onChange={handleEditChange} />
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="section-title-inner mb-3">
                            <span className="inner-icon" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                <i className="ri-calendar-line"></i>
                            </span>
                            <h6>Période & Statut</h6>
                        </div>
                        <Row className="g-3 mb-4">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Date de début</Form.Label>
                                    <Form.Control type="date" name="date_debut" value={editForm.date_debut || ''} onChange={handleEditChange} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Date de fin (vide = CDI)</Form.Label>
                                    <Form.Control type="date" name="date_fin" value={editForm.date_fin || ''} onChange={handleEditChange} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Type de contrat</Form.Label>
                                    <Form.Select name="type_contrat" value={editForm.type_contrat || 'CDD'} onChange={handleEditChange}>
                                        <option value="CDD">CDD</option>
                                        <option value="CDI">CDI</option>
                                        <option value="AVENANT">Avenant</option>
                                        <option value="CERTIFICAT">Certificat</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Statut</Form.Label>
                                    <Form.Select name="statut" value={editForm.statut || 'actif'} onChange={handleEditChange}>
                                        <option value="actif">Actif</option>
                                        <option value="termine">Terminé</option>
                                        <option value="expire">Expiré</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="section-title-inner mb-3">
                            <span className="inner-icon" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                <i className="ri-briefcase-line"></i>
                            </span>
                            <h6>Poste</h6>
                        </div>
                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Qualification</Form.Label>
                                    <Form.Control name="poste_qualification" value={editForm.poste_qualification || ''} onChange={handleEditChange} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Fonction</Form.Label>
                                    <Form.Control name="poste_fonction" value={editForm.poste_fonction || ''} onChange={handleEditChange} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Département</Form.Label>
                                    <Form.Control name="poste_departement" value={editForm.poste_departement || ''} onChange={handleEditChange} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Site</Form.Label>
                                    <Form.Control name="poste_site" value={editForm.poste_site || ''} onChange={handleEditChange} />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="border-0 px-4 pb-3">
                    <Button onClick={() => setShowEditModal(false)} className="btn-modern-outline">
                        Annuler
                    </Button>
                    <Button onClick={handleEdit} disabled={editing} className="btn-modern-primary">
                        {editing ? <><Spinner size="sm" className="me-2" />Enregistrement...</> : <><i className="ri-save-line me-2"></i>Enregistrer</>}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .badge-header-light {
                    background: rgba(255,255,255,0.2); color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px; padding: 0.25rem 0.75rem;
                    font-size: 0.75rem; font-weight: 600;
                }
                .card-header-charte {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border: none; padding: 1rem 1.5rem;
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                }

                .detail-hero { padding: 1.5rem 2rem; color: white; }
                .hero-icon {
                    width: 60px; height: 60px; border-radius: 16px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.8rem;
                    backdrop-filter: blur(10px);
                }
                .hero-label {
                    font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.8px;
                    opacity: 0.85; font-weight: 600;
                }
                .hero-value { font-size: 1.15rem; font-weight: 700; margin-top: 3px; }
                .hero-meta {
                    padding: 0.5rem 1rem;
                    background: rgba(255,255,255,0.15);
                    border-radius: 12px;
                    backdrop-filter: blur(10px);
                }
                .hero-meta .hero-value { font-size: 0.9rem; }
                .detail-progress { height: 8px; border-radius: 4px; background: #f1f5f9; }
                .detail-progress .progress-bar { border-radius: 4px; transition: width 0.6s ease; }

                .partie-card {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 14px;
                    padding: 1.25rem;
                    height: 100%;
                }
                .partie-header {
                    display: flex; align-items: center; gap: 12px;
                    padding-bottom: 12px; margin-bottom: 12px;
                    border-bottom: 1px solid #e2e8f0;
                }
                .partie-icon {
                    width: 40px; height: 40px; border-radius: 10px;
                    display: flex; align-items: center; justify-content: center;
                    color: white; font-size: 1.2rem;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.25);
                }
                .partie-title-main { font-weight: 700; color: #1e293b; font-size: 0.95rem; }
                .partie-body { display: flex; flex-direction: column; gap: 10px; }

                .detail-info-row { display: flex; align-items: center; gap: 12px; }
                .detail-info-icon {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1rem; flex-shrink: 0;
                }
                .detail-info-label {
                    font-size: 0.68rem; text-transform: uppercase;
                    color: #94a3b8; font-weight: 600;
                    letter-spacing: 0.4px;
                }
                .detail-info-value {
                    font-size: 0.85rem; color: #1e293b;
                    font-weight: 600; word-break: break-word;
                }

                .client-highlight {
                    display: flex; align-items: center; gap: 1rem;
                    padding: 1rem 1.25rem;
                    background: linear-gradient(135deg, ${COLORS.secondary}08, ${COLORS.primary}08);
                    border: 2px solid ${COLORS.secondary}40;
                    border-radius: 14px;
                }
                .client-highlight-icon {
                    width: 52px; height: 52px; border-radius: 14px;
                    background: linear-gradient(135deg, var(--secondary), var(--primary-dark));
                    color: white;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem;
                    box-shadow: 0 4px 12px ${COLORS.secondary}40;
                    flex-shrink: 0;
                }
                .client-highlight-label {
                    font-size: 0.7rem; text-transform: uppercase;
                    color: #94a3b8; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .client-highlight-value {
                    font-size: 1rem; font-weight: 700;
                    color: var(--primary-dark);
                    margin-top: 2px;
                }
                .client-highlight-badge {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: white;
                    border: 1.5px solid ${COLORS.secondary}60;
                    color: var(--secondary);
                    padding: 0.4rem 0.85rem;
                    border-radius: 50px;
                    font-size: 0.75rem; font-weight: 700;
                }

                .section-title-inner { display: flex; align-items: center; gap: 0.75rem; }
                .section-title-inner h6 {
                    margin: 0; font-weight: 600;
                    font-size: 0.9rem; color: #1e293b;
                }
                .inner-icon {
                    width: 30px; height: 30px; border-radius: 8px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 0.9rem;
                }

                .missions-list-wrapper { display: flex; flex-direction: column; gap: 8px; }
                .mission-item {
                    display: flex; align-items: flex-start; gap: 12px;
                    padding: 0.75rem 1rem;
                    background: #f8fafc; border-radius: 10px;
                    border-left: 3px solid var(--secondary);
                    transition: all 0.2s ease;
                }
                .mission-item:hover { background: #f1f5f9; transform: translateX(3px); }
                .mission-num {
                    width: 26px; height: 26px; border-radius: 50%;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    color: white;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 0.75rem; font-weight: 700;
                    flex-shrink: 0;
                }
                .mission-text { font-size: 0.85rem; color: #334155; line-height: 1.5; }

                .remun-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
                    gap: 10px;
                }
                .remun-item {
                    display: flex; align-items: center; gap: 12px;
                    padding: 0.85rem 1rem;
                    background: #f8fafc;
                    border: 1px solid #f1f5f9;
                    border-radius: 12px;
                    transition: all 0.2s ease;
                }
                .remun-item:hover {
                    background: #f1f5f9;
                    border-color: ${COLORS.primary}30;
                    transform: translateY(-2px);
                }
                .remun-item-icon {
                    width: 38px; height: 38px; border-radius: 10px;
                    background: linear-gradient(135deg, ${COLORS.primary}15, ${COLORS.secondary}15);
                    color: var(--primary);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.05rem; flex-shrink: 0;
                }
                .remun-item-label { font-size: 0.72rem; color: #64748b; font-weight: 500; }
                .remun-item-value { font-size: 0.9rem; font-weight: 700; color: #1e293b; margin-top: 2px; }
                .remun-item-value span { font-size: 0.7rem; color: #94a3b8; font-weight: 500; }
                .total-remun-card {
                    padding: 1.25rem 1.5rem;
                    background: linear-gradient(135deg, ${COLORS.primary}08, ${COLORS.secondary}08);
                    border: 2px dashed ${COLORS.primary}40;
                    border-radius: 14px;
                }
                .total-remun-label { font-weight: 700; color: var(--primary-dark); font-size: 0.95rem; }
                .total-remun-sublabel { font-size: 0.75rem; color: #94a3b8; }
                .total-remun-value {
                    font-size: 1.6rem; font-weight: 800;
                    color: var(--primary);
                    letter-spacing: -0.5px;
                }
                .total-remun-value span { font-size: 0.85rem; color: #64748b; font-weight: 500; }

                .doc-item {
                    display: flex; align-items: center; gap: 12px;
                    padding: 0.85rem;
                    border-radius: 12px;
                    transition: background 0.2s ease;
                }
                .doc-item:not(:last-child) { border-bottom: 1px solid #f1f5f9; }
                .doc-item:hover { background: #f8fafc; }
                .doc-icon {
                    width: 40px; height: 40px; border-radius: 10px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.2rem; flex-shrink: 0;
                }
                .doc-label { font-size: 0.75rem; color: #64748b; font-weight: 500; }
                .doc-filename {
                    font-size: 0.8rem; color: var(--primary);
                    font-weight: 600; overflow: hidden;
                    text-overflow: ellipsis; white-space: nowrap;
                }
                .doc-empty { font-size: 0.8rem; color: #94a3b8; font-style: italic; }
                .doc-download-btn {
                    width: 34px; height: 34px; border-radius: 10px;
                    background: ${COLORS.success}15;
                    color: var(--success); border: none;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1rem; transition: all 0.2s ease;
                    cursor: pointer;
                }
                .doc-download-btn:hover {
                    background: var(--success); color: white;
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px ${COLORS.success}40;
                }

                .clause-chip {
                    display: flex; align-items: center; gap: 8px;
                    padding: 0.6rem 0.85rem;
                    background: ${COLORS.success}10;
                    border-radius: 10px;
                    margin-bottom: 6px;
                    font-size: 0.82rem;
                    color: #065f46;
                    font-weight: 500;
                }
                .clause-chip i { color: var(--success); }
                .clause-obs {
                    padding: 0.75rem;
                    background: #f8fafc;
                    border-radius: 10px;
                    border-left: 3px solid var(--secondary);
                    font-size: 0.82rem;
                    color: #334155;
                }

                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.3rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; justify-content: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; justify-content: center; gap: 6px;
                }
                .btn-modern-primary:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4);
                    color: white;
                }
                .btn-modern-primary:disabled { opacity: 0.7; }
                .btn-modern-danger {
                    background: linear-gradient(135deg, var(--danger), #dc2626);
                    border: none; color: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(239,68,68,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-danger:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(239,68,68,0.4);
                    color: white;
                }
                .btn-modern-danger:disabled { opacity: 0.7; }

                .delete-summary-card {
                    padding: 1rem 1.25rem;
                    background: #fef2f2;
                    border: 1px solid ${COLORS.danger}20;
                    border-radius: 12px;
                }
                .delete-avatar {
                    width: 52px; height: 52px; border-radius: 12px;
                    background: linear-gradient(135deg, var(--danger), #dc2626);
                    color: white; font-weight: 700; font-size: 1.15rem;
                    display: flex; align-items: center; justify-content: center;
                    flex-shrink: 0;
                }

                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }

                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }

                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                .min-width-0 { min-width: 0; }
                .rounded-3 { border-radius: 12px !important; }

                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .detail-hero { padding: 1.25rem; }
                    .hero-icon { width: 48px; height: 48px; font-size: 1.4rem; }
                    .hero-value { font-size: 1rem; }
                    .hero-meta { flex: 1 1 auto; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                    .total-remun-value { font-size: 1.3rem; }
                }
            `}</style>
        </>
    );
}